import React, {FC} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Input,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from "@material-ui/core";
import {addHours, addMinutes, differenceInMinutes, format, isBefore} from "date-fns";
import {de} from "date-fns/locale";
import {SuggestedAvailability} from "../redux/types";
import {SetStepDataType} from "../types";
import {Info, InfoOutlined} from "@material-ui/icons";
import {Theme} from "@material-ui/core/styles";

interface SuggestedAvailabilityDialogProps {
    showDialog: boolean;
    toggleDialog: () => void;
    suggestedAvailability: SuggestedAvailability;
    servicesIds: string[];
    onConfirm: (v: SetStepDataType) => void;
}

function get15MinutesIncrements(start: Date | number, end: Date | number): Date[] {
    let startDate = addHours(start, 2);
    const endDate = new Date(end);

    const result: Date[] = [startDate];

    while (isBefore(startDate, endDate)) {
        const newStartDate = addMinutes(startDate, 15)
        result.push(newStartDate);
        startDate = newStartDate;
    }

    return result;
}

const getFormatedDifference = (start: Date | number, end: Date | number): string => {
    const allMinutes = differenceInMinutes(end, start);
    const hours = Math.floor(allMinutes / 60);
    const minutes = allMinutes % 60;
    return `${hours} Std. ${minutes > 0 ? minutes + " Min." : ""}`
}

export const SuggestedAvailabilityDialog: FC<SuggestedAvailabilityDialogProps> = ({
                                                                                      showDialog,
                                                                                      toggleDialog,
                                                                                      suggestedAvailability,
                                                                                      onConfirm
                                                                                  }) => {

    const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

    const [endDate, setEndDate] = React.useState<number>(addHours(suggestedAvailability.from, 2).getTime());

    const endDateOptions = get15MinutesIncrements(suggestedAvailability.from, suggestedAvailability.to);

    const minutesOfOrder = differenceInMinutes(endDate, suggestedAvailability.from);
    const hours = Math.floor(minutesOfOrder / 60);
    const minutes = minutesOfOrder % 60;


    return (
        <Dialog open={showDialog} maxWidth="sm" onClose={toggleDialog}>
            <DialogTitle>
                <Typography variant="inherit" color="secondary">
                    Wie lange benötigen Sie die Hilfe?
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography color="textSecondary" variant="body1">
                    {format(suggestedAvailability.from, "EEEE, dd.MM.yy", {locale: de})}
                </Typography>
                <div style={{display: "flex"}}>
                    <div style={{justifyContent: "space-between", marginBottom: 16, marginTop: 16, flex: 1}}>
                        <div style={{width: "100%", marginBottom: 8}}>
                            <TextField
                                label={<Typography variant="inherit">Start<Tooltip
                                    title={<Typography>Zu dieser Uhrzeit wird die von Ihnen ausgewählte Gartenhilfe bei
                                        Ihnen eintreffen. Eine andere Uhrzeit ist als Start nicht auswählbar, da dies
                                        mit anderen Terminen kollidieren würde.</Typography>}><InfoOutlined
                                    style={{marginLeft: 8, textAlign: "center"}} color="primary"
                                    fontSize="inherit"/></Tooltip></Typography>}
                                fullWidth
                                value={format(suggestedAvailability.from, "HH:mm")}
                            />
                        </div>
                        {suggestedAvailability &&
                        <FormControl style={{width: "100%"}}>
                            <InputLabel id="demo-dialog-select-label">ca. Ende<Tooltip
                                title={<Typography>Die Termindauer beträgt mindestens zwei volle Stunden, damit sich die
                                    Anfahrt für die Gartenhilfe lohnt. Danach rechnen wir im Viertelstundentakt ab.
                                </Typography>}><InfoOutlined
                                style={{marginLeft: 8, textAlign: "center"}} color="primary"
                                fontSize="inherit"/></Tooltip></InputLabel>
                            <Select
                                labelId="demo-dialog-select-label"
                                id="demo-dialog-select"
                                value={endDate}
                                input={<Input/>}
                                onChange={(e) => setEndDate(e.target.value as number)}
                            >
                                {endDateOptions.map((eDo, i) =>
                                    <MenuItem value={eDo.getTime()} key={"date-option-" + i}>
                                        {format(eDo, "HH:mm")}
                                        <Typography
                                            color="textSecondary"
                                            variant="inherit"
                                            style={{marginLeft: 16}}
                                        >
                                            {getFormatedDifference(suggestedAvailability.from, eDo)}
                                        </Typography>
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>}
                    </div>
                    <div style={{display: "flex", alignItems: "center", justifyContent: "center", flex: 1}}>
                        <div>
                            <Typography variant="body2"
                                        color="textSecondary">{isDesktop && "Ihre "}Termindauer</Typography>
                            <Typography variant="h6" color="primary">
                                {hours} Std.{isDesktop ? " " :
                                <br/>}{minutes > 0 && minutes + " Min."}
                            </Typography>
                        </div>
                    </div>
                </div>
                <div style={{marginBottom: 16, display: "flex", marginTop: 24}}>
                    <Info color="primary"/>
                    <Typography style={{marginLeft: 8}} color="textSecondary">
                        Ihre Hilfe wird zur vereinbarten Uhrzeit bei Ihnen eintreffen und Ihnen so lange behilflich
                        sein, wie Sie sie benötigen
                    </Typography>
                </div>
                <DialogActions>
                    <Button
                        color="inherit"
                        variant="outlined"
                        onClick={toggleDialog}
                    >
                        Zurück
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onConfirm({
                            timeRange: {
                                estimatedFrom: suggestedAvailability.from,
                                estimatedTo: endDate
                            },
                            worker: {
                                workerId: suggestedAvailability.workerId,
                                workerName: suggestedAvailability.workerName
                            }
                        })}
                    >
                        Weiter
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}
